/*
 * sk-lazy-section — defers below-the-fold section content until the shopper
 * scrolls near it. Wrap a section's markup in <sk-lazy-section> and put the
 * deferred markup inside a <template>. When the element nears the viewport,
 * the template is cloned into place and any lazy images/scripts activate.
 *
 * Usage (see snippet 'lazy-section'):
 *   <sk-lazy-section data-rootmargin="600px">
 *     <template>...heavy section markup...</template>
 *     <div class="lazy-section__placeholder" style="min-height:..."></div>
 *   </sk-lazy-section>
 */
if (!customElements.get('sk-lazy-section')) {
  class SkLazySection extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template');
      this.placeholder = this.querySelector('.lazy-section__placeholder');
      if (!this.template) return;

      // If IntersectionObserver is unavailable, render immediately (progressive enhancement).
      if (!('IntersectionObserver' in window)) {
        this.reveal();
        return;
      }

      const rootMargin = this.dataset.rootmargin || '600px 0px';
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.reveal();
            break;
          }
        }
      }, { rootMargin });

      this.observer.observe(this);
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.observer?.disconnect();

      const fragment = this.template.content.cloneNode(true);

      // Move any <script> tags out so cloning actually executes them.
      const scripts = Array.from(fragment.querySelectorAll('script'));

      this.template.remove();
      this.placeholder?.remove();
      this.appendChild(fragment);

      // Re-insert scripts as fresh nodes so the browser runs them.
      scripts.forEach((old) => {
        const s = document.createElement('script');
        for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
        s.textContent = old.textContent;
        old.replaceWith(s);
      });

      // Nudge lazysizes / native lazy images that just entered the DOM.
      if (window.lazySizes && typeof window.lazySizes.loader?.checkElems === 'function') {
        window.lazySizes.loader.checkElems();
      }

      this.dispatchEvent(new CustomEvent('lazy-section:revealed', { bubbles: true }));
      // Let theme scripts that hydrate on section load pick it up.
      document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: this.dataset.sectionId } }));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  customElements.define('sk-lazy-section', SkLazySection);
}
